import dayjs from 'dayjs';
import React from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@material-ui/core';

import DateDropdown from '../../components/DateDropdown/DateDropdown';
import { MarketsSelect } from '../../components/FilterBars/MarketsSelect';
import { PageWrapper } from '../../components/PageWrapper';
import { useAdminRole } from '../../hooks/useAdminRole';
import { getTimeOfDay } from '../../utils/time-of-day';
import { Collabs } from './Collabs';
import { Content } from './Content';
import { CoreAction } from './CoreAction';
import { Hello, Note, TimeOfDay } from './HomePageStyled';
import { ModelApplicationsV2 } from './ModelApplicationsV2';
import { ReservationCategory } from './ReservationCategory';
import { ReservationsV2 } from './ReservationsV2';
import TotalRevenueCard from './TotalRevenueCard';
import { Totals } from './Totals';
import { Users } from './Users';
import { VenueStats } from './VenueStats';
import { VenuesV2 } from './VenuesV2';

export const Salutation: React.FC = () => {
  const { user } = useAdminRole();
  const name = user?.attributes?.firstname || user?.attributes?.email || '';

  return (
    <div>
      <Hello>
        Hi{name ? ` ${name}, ` : ', '}
        <TimeOfDay>{getTimeOfDay()}</TimeOfDay>
      </Hello>
      <Note>
        Good luck with your work on <strong>Beautypass</strong> today!
      </Note>
    </div>
  );
};

const SupportHomePage: React.FC = () => {
  return (
    <PageWrapper>
      <Salutation />
    </PageWrapper>
  );
};

const AdminHomePage: React.FC = () => {
  const { isSuperAdmin } = useAdminRole();

  const [fromDate, setFromDate] = React.useState<string | null>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [priorFrom, setPriorFrom] = React.useState<string | null>(
    dayjs().subtract(60, 'day').format('YYYY-MM-DD'),
  );
  const [toDate, setToDate] = React.useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [priorTo, setPriorTo] = React.useState<string | null>(
    dayjs().subtract(31, 'day').format('YYYY-MM-DD'),
  );
  const [selectedLabel, setSelectedLabel] = React.useState('Last 30 days'); // State to store the label
  const [marketID, setMarketID] = React.useState<string | null>(null); // State to store the label

  const handleMarketChange = React.useCallback((value?: string) => {
    if (value === 'all') return setMarketID(null);
    setMarketID(value || '');
  }, []);

  return (
    <PageWrapper>
      <DashboardContainer>
        <HideOnMobile>
          <DashboardTopSection>
            <DateDropdown
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              selectedLabel={selectedLabel}
              setSelectedLabel={setSelectedLabel}
              setPriorFrom={setPriorFrom}
              setPriorTo={setPriorTo}
            />
            <MarketsSelect value={marketID || 'all'} handleOnChange={handleMarketChange} />
          </DashboardTopSection>
        </HideOnMobile>

        <Totals
          priorFrom={priorFrom}
          fromDate={fromDate}
          toDate={toDate}
          priorTo={priorTo}
          marketID={marketID}
          selectedLabel={selectedLabel}
        />

        <HideOnMobile>
          <ModelApplicationsV2
            fromDate={fromDate}
            toDate={toDate}
            selectedLabel={selectedLabel}
            marketID={marketID}
          />
          <VenuesV2
            priorFrom={priorFrom}
            fromDate={fromDate}
            toDate={toDate}
            priorTo={priorTo}
            selectedLabel={selectedLabel}
            marketID={marketID}
          />
          <CoreAction fromDate={fromDate} toDate={toDate} />
          <SummaryCardGridDesktopStats>
            <Box>
              <ReservationsV2
                selectedLabel={selectedLabel}
                fromDate={fromDate}
                priorFrom={priorFrom}
                toDate={toDate}
                priorTo={priorTo}
                marketID={marketID}
              />
              <ReservationCategory
                selectedLabel={selectedLabel}
                fromDate={fromDate}
                priorFrom={priorFrom}
                toDate={toDate}
                priorTo={priorTo}
                marketID={marketID}
              />
              <Users />
            </Box>
            <Box>
              <VenueStats
                selectedLabel={selectedLabel}
                fromDate={fromDate}
                priorFrom={priorFrom}
                toDate={toDate}
                priorTo={priorTo}
                marketID={marketID}
              />
              <Collabs
                selectedLabel={selectedLabel}
                fromDate={fromDate}
                priorFrom={priorFrom}
                toDate={toDate}
                priorTo={priorTo}
                marketID={marketID}
              />
              <Content
                selectedLabel={selectedLabel}
                fromDate={fromDate}
                priorFrom={priorFrom}
                toDate={toDate}
                priorTo={priorTo}
                marketID={marketID}
              />
            </Box>
          </SummaryCardGridDesktopStats>
          {isSuperAdmin && (
            <SummaryCardGridDesktopStats>
              <TotalRevenueCard
                fromDate={fromDate}
                toDate={toDate}
                priorFrom={priorFrom}
                priorTo={priorTo}
                selectedLabel={selectedLabel}
              />
            </SummaryCardGridDesktopStats>
          )}
        </HideOnMobile>
      </DashboardContainer>
    </PageWrapper>
  );
};

export const HomePage: React.FC = () => {
  const { isCityManager, isSupport, isVenue, venueId, isSocial, isFastCheck, isSuperAdmin } =
    useAdminRole();

  if (isVenue) {
    return <Redirect to={`/businesses/${venueId}/profile`} />;
  } else if (isSocial) {
    return <Redirect to={`/content`} />;
  } else {
    if (isSupport || isCityManager || isFastCheck) {
      return <SupportHomePage />;
    } else if (isSuperAdmin) {
      return <AdminHomePage />;
    } else {
      return <SupportHomePage />;
    }
  }
};

const HideOnMobile = styled.div`
  @media (max-width: 487px) {
    display: none;
  }
`;

const DashboardContainer = styled.div`
  padding: 20px 60px;
  font-family: 'Poppins';
  @media (max-width: 487px) {
    padding: 0;
  }
`;

const SummaryCardGridDesktopStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DashboardTopSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 24px;
`;
